import { Box, HStack, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';

import { AccessBadge } from '#features/media/AccessBadge';
import { Chip } from '#features/ui';
import { useMicDenialHint } from '#features/media/useMicDenialHint';
import { envConfig } from '#constants/envConfig';
import { openAppSettings } from '#features/notifications/utils/deviceSettings';
import MaximizeIcon from '~icons/lucide/maximize';
import MicIcon from '~icons/lucide/mic';
import MicOffIcon from '~icons/lucide/mic-off';
import MinimizeIcon from '~icons/lucide/minimize';
import MonitorUpIcon from '~icons/lucide/monitor-up';
import PhoneOffIcon from '~icons/lucide/phone-off';
import VideoIcon from '~icons/lucide/video';
import VideoOffIcon from '~icons/lucide/video-off';
import ZoomInIcon from '~icons/lucide/zoom-in';
import ZoomOutIcon from '~icons/lucide/zoom-out';
import { useEffect, useRef, useState } from 'react';

import type { useCallRoom } from '#features/calls/hooks/useCallRoom';
import type { CallInfo } from '#features/calls/model/callStore';
import { Avatar } from '#features/ui/components/Avatar';
import { Button } from '#features/ui/components/Button';
import { IconButton } from '#features/ui/components/IconButton';

// Surfaces come from the theme's brand scale rather than one-off hexes, so the call looks
// like the rest of the product instead of a widget that wandered in from another app.
const STAGE_BG = 'bg';
const TILE_BG = 'bg.card';
const TILE_HOVER = 'bg.emphasized';
const VOICE_GREEN = 'green.400';

/** iOS wants 44pt, Android 48dp; 40px plus the row's padding clears both. */
const HIT_SIZE = '40px';

type RoomApi = ReturnType<typeof useCallRoom>;

type CallStageProps = {
  call: CallInfo;
  /** 'embedded' — docked at the top of the chat thread; 'floating' — desktop window / mobile fullscreen. */
  variant: 'embedded' | 'floating';
  phase: 'outgoing' | 'active';
  duration: string;
  micEnabled: boolean;
  camEnabled: boolean;
  onToggleMic: () => void;
  onToggleCam: () => void;
  onHangup: () => void;
  room: RoomApi;
  /** Current user's avatar for the "You" participant row. */
  selfAvatar?: string | null;
  /** Fullscreen: let the video area stretch to fill the window. */
  fill?: boolean;
  /** Extra element rendered at the right of the status row (device menu, minimize…). */
  headerExtra?: React.ReactNode;
  /** Открыть список устройств — вызывается после того, как разрешение только что выдали. */
  onOpenDeviceMenu?: () => void;
};

type ParticipantRowProps = {
  name: string;
  avatar: string | null;
  speaking: boolean;
  micOn: boolean;
  subtitle: string;
};

/**
 * Speaking is marked by a calm rail down the left edge instead of a neon halo around the
 * avatar: it survives at 26px, it does not compete with the video for attention, and it
 * costs one composited element rather than a repainting glow.
 */
const ParticipantRow = ({ name, avatar, speaking, micOn, subtitle }: ParticipantRowProps) => (
  <HStack
    position="relative"
    px={2}
    py={1.5}
    gap={2.5}
    borderRadius="lg"
    _hover={{ bg: TILE_HOVER }}
    transition="background 0.15s"
    _before={{
      content: '""',
      position: 'absolute',
      left: 0,
      top: '10px',
      bottom: '10px',
      width: '2px',
      borderRadius: 'full',
      bg: speaking ? VOICE_GREEN : 'transparent',
      transition: 'background 0.15s',
    }}
  >
    <Avatar
      size="xs"
      src={avatar}
      name={name}
      borderRadius="lg"
      css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
    />
    <Stack gap={0} minW={0} flex={1}>
      <Text textStyle="label" lineClamp={1}>
        {name}
      </Text>
      <Text textStyle="caption" color={speaking ? VOICE_GREEN : 'fg.muted'} lineClamp={1}>
        {subtitle}
      </Text>
    </Stack>
    {!micOn && (
      <Box color="red.400" fontSize="14px">
        <MicOffIcon />
      </Box>
    )}
  </HStack>
);

/**
 * The in-call panel: who you are talking to and for how long, a video area when anyone
 * publishes camera or screen, a row of equal-width controls, and the participant list.
 * Rendered either docked at the top of the chat thread (embedded) or inside the floating
 * window.
 */
export const CallStage = ({
  call,
  variant,
  phase,
  duration,
  micEnabled,
  camEnabled,
  onToggleMic,
  onToggleCam,
  onHangup,
  room,
  selfAvatar = null,
  fill = false,
  headerExtra,
  onOpenDeviceMenu,
}: CallStageProps) => {
  const { t } = useLingui();
  const micDenialHint = useMicDenialHint();
  const {
    remoteCameraRef,
    remoteScreenRef,
    localVideoRef,
    localScreenRef,
    connectionState,
    remoteJoined,
    remoteVideoOn,
    remoteScreenOn,
    remoteMicOn,
    localSpeaking,
    remoteSpeaking,
    peerGone,
    screenSharing,
    screenSharePending,
    screenShareHint,
    mediaError,
    camDenied,
    audioBlocked,
    unblockAudio,
  } = room;

  const peerName = call.peer.displayName || t`Player`;
  const showVideoArea =
    phase === 'active' && (remoteVideoOn || remoteScreenOn || camEnabled || screenSharing);
  const connected = phase === 'active' && connectionState === 'connected';

  // Tile spotlight: every live stream renders permanently (its own <video>), CSS
  // decides who is the big one. Click a thumbnail to pin it; auto priority
  // otherwise: peer screen > peer camera > my camera. Pin resets if the stream ends.
  type StreamKey = 'remoteScreen' | 'remoteCam' | 'localScreen' | 'localCam';
  const liveStreams: StreamKey[] = [
    ...(remoteScreenOn ? (['remoteScreen'] as const) : []),
    ...(remoteVideoOn ? (['remoteCam'] as const) : []),
    ...(screenSharing ? (['localScreen'] as const) : []),
    ...(camEnabled ? (['localCam'] as const) : []),
  ];
  const [pinned, setPinned] = useState<StreamKey | null>(null);
  useEffect(() => {
    if (pinned && !liveStreams.includes(pinned)) {
      setPinned(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [remoteScreenOn, remoteVideoOn, camEnabled, screenSharing]);
  const mainStream: StreamKey | null =
    pinned && liveStreams.includes(pinned) ? pinned : (liveStreams[0] ?? null);
  const thumbs = liveStreams.filter((key) => key !== mainStream);

  // Meet-style viewing controls for the spotlight: browser fullscreen on the video
  // area, and +/- zoom with drag-to-pan for screen shares (small text readability).
  const videoAreaRef = useRef<HTMLDivElement | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [pan, setPanState] = useState({ x: 0, y: 0 });
  const zoomable = mainStream === 'remoteScreen' || mainStream === 'localScreen';
  const fullscreenAvailable =
    typeof document !== 'undefined' && Boolean(document.fullscreenEnabled);

  useEffect(() => {
    const onFullscreenChange = () =>
      setIsFullscreen(
        Boolean(document.fullscreenElement) && document.fullscreenElement === videoAreaRef.current
      );
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => {
    setZoom(1);
    setPanState({ x: 0, y: 0 });
  }, [mainStream]);

  const clampPan = (next: { x: number; y: number }, atZoom: number) => {
    const rect = videoAreaRef.current?.getBoundingClientRect();
    if (!rect) {
      return next;
    }
    const maxX = ((atZoom - 1) * rect.width) / 2;
    const maxY = ((atZoom - 1) * rect.height) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, next.x)),
      y: Math.max(-maxY, Math.min(maxY, next.y)),
    };
  };

  const changeZoom = (delta: number) => {
    const next = Math.max(1, Math.min(4, zoom + delta));
    setZoom(next);
    setPanState(next === 1 ? { x: 0, y: 0 } : (prev) => clampPan(prev, next));
  };

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
      return;
    }
    videoAreaRef.current?.requestFullscreen().catch(() => undefined);
  };

  /** Pan a zoomed screen share by dragging the spotlight. */
  const onSpotlightPanStart = (event: React.PointerEvent) => {
    if (!zoomable || zoom <= 1) {
      return;
    }
    event.preventDefault();
    const startX = event.clientX;
    const startY = event.clientY;
    const from = pan;
    const onMove = (move: PointerEvent) => {
      setPanState(
        clampPan({ x: from.x + (move.clientX - startX), y: from.y + (move.clientY - startY) }, zoom)
      );
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const statusLabel =
    phase === 'outgoing'
      ? t`Ringing…`
      : connectionState === 'reconnecting'
        ? t`Reconnecting…`
        : connectionState === 'connecting'
          ? t`Connecting…`
          : t`Connected`;

  const controlButton = (options: {
    label: string;
    active: boolean;
    onClick: () => void;
    iconOn: React.ReactNode;
    iconOff: React.ReactNode;
    /** Устройство запрещено браузером или системой: кнопка носит метку и просит доступ. */
    needsAccess?: boolean;
  }) => (
    <IconButton
      aria-label={options.label}
      onClick={options.onClick}
      borderRadius="xl"
      flex={1}
      minW={HIT_SIZE}
      h="44px"
      // Метка садится на угол кнопки, поэтому кнопке нужны точка отсчёта и право вылезать за
      // свои границы: у IconButton по умолчанию нет ни того, ни другого.
      position="relative"
      overflow="visible"
      bg={options.active ? 'whiteAlpha.100' : 'red.500/16'}
      color={options.active ? 'fg' : 'red.400'}
      borderWidth="1px"
      borderColor={options.active ? 'border' : 'red.500/35'}
      _hover={{ bg: options.active ? 'whiteAlpha.200' : 'red.500/24' }}
    >
      {options.active ? options.iconOn : options.iconOff}
      {options.needsAccess ? <AccessBadge /> : null}
    </IconButton>
  );

  return (
    <Box
      bg={STAGE_BG}
      borderRadius={variant === 'embedded' ? 'lg' : 'none'}
      overflow="hidden"
      borderWidth={variant === 'embedded' ? '1px' : 0}
      borderColor="border"
      boxShadow={variant === 'embedded' ? 'lg' : 'none'}
      display="flex"
      flexDirection="column"
      h={fill ? '100%' : 'auto'}
    >
      {/* Status row: who you are talking to, then how it is going. A 6px dot and tabular
          digits say what a glowing "VOICE CONNECTED" in caps used to shout. */}
      <HStack px={3} pt={3} pb={2} gap={2.5} align="center">
        <Avatar
          size="xs"
          src={call.peer.avatar}
          name={peerName}
          borderRadius="lg"
          flexShrink={0}
          css={{ '--avatar-radius': 'var(--chakra-radii-lg)' }}
        />
        <Stack gap={0} minW={0} flex={1}>
          <Text textStyle="title" lineClamp={1}>
            {peerName}
          </Text>
          <HStack gap={1.5} minW={0} color="fg.muted">
            <Box
              w="6px"
              h="6px"
              borderRadius="full"
              flexShrink={0}
              bg={connected ? VOICE_GREEN : 'fg.subtle'}
            />
            {/*
              Длительность режется отдельным элементом и не сжимается: одной строкой «Connected ·
              0:42» в узкой шапке окна обрезалось ровно то, ради чего на неё и смотрят, — в
              многоточие уходило время, а оставалось слово «Connected…». Теперь при нехватке
              места сокращается подпись состояния, а секунды остаются на экране.
            */}
            <Text
              textStyle="caption"
              whiteSpace="nowrap"
              overflow="hidden"
              textOverflow="ellipsis"
              minW={0}
            >
              {statusLabel}
            </Text>
            {connected ? (
              <Text
                textStyle="caption"
                fontVariantNumeric="tabular-nums"
                whiteSpace="nowrap"
                flexShrink={0}
              >
                {` · ${duration}`}
              </Text>
            ) : null}
          </HStack>
        </Stack>
        {headerExtra}
        <IconButton
          aria-label={t`End call`}
          size="sm"
          minW={HIT_SIZE}
          h={HIT_SIZE}
          borderRadius="lg"
          bg="transparent"
          color="red.400"
          _hover={{ bg: 'red.500/16' }}
          onClick={onHangup}
        >
          <PhoneOffIcon />
        </IconButton>
      </HStack>

      {/* Video area: main spotlight + clickable thumbnails, all elements permanent */}
      {showVideoArea && (
        <Box
          ref={videoAreaRef}
          position="relative"
          bg="black"
          mx={2}
          borderRadius="md"
          overflow="hidden"
          css={{ '&:fullscreen': { aspectRatio: 'auto', borderRadius: 0, margin: 0 } }}
          {...(fill ? { flex: 1, minH: 0 } : { aspectRatio: 16 / 9 })}
        >
          {[
            {
              key: 'remoteScreen' as const,
              ref: remoteScreenRef,
              live: remoteScreenOn,
              mirror: false,
              label: t`${peerName} - screen`,
            },
            {
              key: 'remoteCam' as const,
              ref: remoteCameraRef,
              live: remoteVideoOn,
              mirror: false,
              label: peerName,
            },
            {
              key: 'localScreen' as const,
              ref: localScreenRef,
              live: screenSharing,
              mirror: false,
              label: t`You - screen`,
            },
            {
              key: 'localCam' as const,
              ref: localVideoRef,
              live: camEnabled,
              mirror: true,
              label: t`You`,
            },
          ].map((stream, _index) => {
            const isMain = stream.key === mainStream;
            const thumbIndex = thumbs.indexOf(stream.key);
            return (
              <Box
                key={stream.key}
                position="absolute"
                {...(isMain
                  ? { inset: 0 }
                  : {
                      right: 2,
                      bottom: `${8 + thumbIndex * 76}px`,
                      w: '116px',
                      h: '68px',
                      borderRadius: 'md',
                      overflow: 'hidden',
                      borderWidth: '1px',
                      borderColor: 'whiteAlpha.400',
                      cursor: 'pointer',
                      _hover: { borderColor: 'fg.secondary' },
                      zIndex: 2,
                    })}
                display={stream.live ? 'block' : 'none'}
                bg="black"
                onClick={!isMain && stream.live ? () => setPinned(stream.key) : undefined}
                onPointerDown={isMain ? onSpotlightPanStart : undefined}
                cursor={isMain && zoomable && zoom > 1 ? 'grab' : undefined}
                title={!isMain ? t`Click to enlarge` : undefined}
              >
                <video
                  ref={stream.ref}
                  autoPlay
                  playsInline
                  muted={stream.key === 'localCam'}
                  style={{
                    width: '100%',
                    height: '100%',
                    // A shared screen must be visible edge-to-edge (letterboxed);
                    // cameras look better cropped to fill.
                    objectFit:
                      stream.key === 'remoteScreen' || stream.key === 'localScreen'
                        ? 'contain'
                        : 'cover',
                    transform:
                      isMain && zoomable && zoom > 1
                        ? `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`
                        : stream.mirror
                          ? 'scaleX(-1)'
                          : 'none',
                  }}
                >
                  <track kind="captions" />
                </video>
                <Chip
                  variant="media"
                  size="sm"
                  position="absolute"
                  bottom={1}
                  left={1.5}
                  textStyle={isMain ? 'xs' : '2xs'}
                  pointerEvents="none"
                >
                  {stream.label}
                </Chip>
              </Box>
            );
          })}
          {mainStream !== null && (
            <HStack position="absolute" top={2} left={2} zIndex={3} gap={1}>
              {zoomable && (
                <>
                  <IconButton
                    aria-label={t`Zoom out`}
                    size="xs"
                    borderRadius="lg"
                    bg="blackAlpha.600"
                    /* Сплошной white, а не whiteAlpha.800: у лупы ручка касается окружности,
                       и на полупрозрачном цвете альфа на стыке складывалась — стык уезжал к
                       белому пятном. Заодно контраст к кадру растёт (на светлом кадре 4.9 -> 6.5):
                       кнопка лежит поверх видео, где фон меняется от кадра к кадру. */
                    color="white"
                    _hover={{ bg: 'blackAlpha.800' }}
                    disabled={zoom <= 1}
                    onClick={() => changeZoom(-0.5)}
                  >
                    <ZoomOutIcon />
                  </IconButton>
                  {zoom > 1 && (
                    <Text
                      textStyle="2xs"
                      color="whiteAlpha.800"
                      bg="blackAlpha.600"
                      px={1.5}
                      py={1}
                      borderRadius="md"
                      fontVariantNumeric="tabular-nums"
                    >
                      {Math.round(zoom * 100)}%
                    </Text>
                  )}
                  <IconButton
                    aria-label={t`Zoom in`}
                    size="xs"
                    borderRadius="lg"
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: 'blackAlpha.800' }}
                    disabled={zoom >= 4}
                    onClick={() => changeZoom(0.5)}
                  >
                    <ZoomInIcon />
                  </IconButton>
                </>
              )}
              {fullscreenAvailable && (
                <IconButton
                  aria-label={isFullscreen ? t`Exit fullscreen` : t`Enter fullscreen`}
                  size="xs"
                  borderRadius="lg"
                  bg="blackAlpha.600"
                  color="white"
                  _hover={{ bg: 'blackAlpha.800' }}
                  onClick={toggleFullscreen}
                >
                  {isFullscreen ? <MinimizeIcon /> : <MaximizeIcon />}
                </IconButton>
              )}
            </HStack>
          )}
          {mainStream === null && (
            <Stack position="absolute" inset={0} align="center" justify="center" bg={TILE_BG}>
              <Box
                borderRadius="3xl"
                p="3px"
                outlineWidth="2px"
                outlineStyle="solid"
                outlineColor={remoteSpeaking ? VOICE_GREEN : 'transparent'}
                transition="outline-color 0.15s"
              >
                <Avatar
                  size="lg"
                  src={call.peer.avatar}
                  name={peerName}
                  borderRadius="3xl"
                  css={{ '--avatar-radius': 'var(--chakra-radii-3xl)' }}
                />
              </Box>
            </Stack>
          )}
          {peerGone && (
            <Chip
              variant="media"
              position="absolute"
              top={2}
              left="50%"
              transform="translateX(-50%)"
              textStyle="xs"
              zIndex={3}
            >
              {t`${peerName} is reconnecting…`}
            </Chip>
          )}
        </Box>
      )}

      {/* Square control buttons row */}
      {phase === 'active' && (
        <HStack px={3} py={2} gap={2}>
          {controlButton({
            label: mediaError
              ? t`media:access.mic.missing`
              : micEnabled
                ? t`Mute microphone`
                : t`Unmute microphone`,
            active: micEnabled,
            needsAccess: mediaError,
            // Запрещённое устройство сначала просит доступ и только потом переключается: жать
            // «включить» у микрофона, которому браузер отказал, бессмысленно — до правки это
            // ровно так и работало, кнопка гасла обратно без единого слова.
            onClick: mediaError
              ? () => {
                  void (async () => {
                    if (await room.requestMicAccess()) {
                      onOpenDeviceMenu?.();
                    }
                  })();
                }
              : onToggleMic,
            iconOn: <MicIcon />,
            iconOff: <MicOffIcon />,
          })}
          {controlButton({
            label: camDenied
              ? t`media:access.camera.missing`
              : camEnabled
                ? t`Turn camera off`
                : t`Turn camera on`,
            active: camEnabled,
            needsAccess: camDenied,
            onClick: camDenied
              ? () => {
                  void (async () => {
                    if (await room.requestCameraAccess()) {
                      onOpenDeviceMenu?.();
                    }
                  })();
                }
              : onToggleCam,
            iconOn: <VideoIcon />,
            iconOff: <VideoOffIcon />,
          })}
          <IconButton
            aria-label={
              screenSharePending
                ? t`Waiting for the screen picker…`
                : screenSharing
                  ? t`Stop sharing screen`
                  : t`Share screen`
            }
            onClick={() => void room.toggleScreenShare()}
            loading={screenSharePending}
            borderRadius="xl"
            flex={1}
            minW={HIT_SIZE}
            h="44px"
            display={{ base: 'none', md: 'inline-flex' }}
            bg={screenSharing ? VOICE_GREEN : 'whiteAlpha.100'}
            color={screenSharing ? 'green.950' : 'fg'}
            borderWidth="1px"
            borderColor={screenSharing ? 'transparent' : 'border'}
            _hover={{ bg: screenSharing ? 'green.300' : 'whiteAlpha.200' }}
          >
            <MonitorUpIcon />
          </IconButton>
        </HStack>
      )}

      {/* Звук заблокирован браузером. Это единственная плашка здесь с кнопкой: остальные
          сообщают, а этой человек чинит свою тишину одним нажатием — Safari и WKWebView
          разрешают воспроизведение только из жеста. Кнопка размером в HIT_SIZE по высоте,
          чтобы по ней попадали пальцем в приложении, а не только курсором. */}
      {audioBlocked && (
        <HStack px={3} pb={1} gap={2} justifyContent="space-between">
          <Text textStyle="caption" color="fg.warning">
            {t`Sound is blocked by the browser`}
          </Text>
          <Button size="xs" minH={HIT_SIZE} px={3} onClick={() => void unblockAudio()}>
            {t`Turn on sound`}
          </Button>
        </HStack>
      )}

      {/* Совет без кнопки — это путь из четырёх шагов, набранный текстом; в приложении он есть
          куда вести одним тапом, и вести надо отсюда: на Android ≤12 системная проверка
          разрешений отвечает «выдано» даже про отобранное, поэтому предупредить заранее там
          нельзя, и этот момент — единственный, когда человек узнаёт о запрете. */}
      {mediaError && (
        <HStack px={3} pb={1} gap={2} justifyContent="space-between" alignItems="flex-start">
          <Text textStyle="caption" color="fg.warning">
            {micDenialHint}
          </Text>
          {envConfig.IS_NATIVE && (
            <Button
              size="xs"
              minH={HIT_SIZE}
              px={3}
              flex="none"
              onClick={() => void openAppSettings()}
            >
              {t`Open settings`}
            </Button>
          )}
        </HStack>
      )}

      {screenShareHint && (
        <Text textStyle="caption" color="fg.warning" px={3} pb={1}>
          {screenShareHint === 'permission'
            ? t`Screen sharing didn't start. If you didn't cancel it yourself, allow screen recording for your browser in system settings and try again`
            : t`Couldn't start screen sharing - try again`}
        </Text>
      )}

      {/* Participants */}
      <Stack px={2} pb={2} gap={0}>
        <ParticipantRow
          name={peerName}
          avatar={call.peer.avatar}
          speaking={remoteSpeaking}
          micOn={remoteMicOn}
          subtitle={
            phase === 'outgoing'
              ? t`Ringing…`
              : peerGone
                ? t`Reconnecting…`
                : remoteJoined
                  ? t`In voice`
                  : t`Connecting…`
          }
        />
        {phase === 'active' && (
          <ParticipantRow
            name={t`You`}
            avatar={selfAvatar}
            speaking={localSpeaking}
            micOn={micEnabled}
            subtitle={t`In voice`}
          />
        )}
      </Stack>
    </Box>
  );
};
